import { useContext, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import {
  CalendarIcon,
  EditIcon,
  MailIcon,
  PhoneIcon,
} from '../../../assets/icons/icons';
import {
  formatDateOfBirthDisplay,
  normalizeDateOfBirthApiValue,
} from '../../../assets/utils/dateOfBirth';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import { formatConfirmedPhone } from '../../../assets/utils/phone';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import TelegramUserAvatar from '../../shared/TelegramUserAvatar/TelegramUserAvatar';
import '../profileSubpage.css';
import PersonalInfoEditSheet from './PersonalInfoEditSheet';
import './ProfilePersonalInfo.css';

function getPersonalDataValue(currentUser, keys) {
  const personalData = currentUser?.personal_data || {};

  for (const key of keys) {
    const value = personalData[key] ?? currentUser?.[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

function getTelegramFullName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ');
}

function getTelegramUsername(user) {
  const username = String(user?.username || '').trim().replace(/^@/, '');

  return username ? `@${username}` : '';
}

function InfoRow({ Icon, label, onClick, value }) {
  const handleClick = () => {
    triggerImpactHaptic('light');
    onClick?.();
  };

  return (
    <button
      type="button"
      className="profile-subpage__row profile-personal__field-row"
      onClick={handleClick}
    >
      <Icon className="profile-personal__field-icon" />
      <span className="profile-personal__field-copy">
        <span className="profile-personal__field-label">{label}</span>
        <span className="profile-personal__field-value" title={value}>{value}</span>
      </span>
      <EditIcon className="profile-personal__edit-icon" />
    </button>
  );
}

function ProfilePersonalInfo() {
  const navigate = useNavigate();
  const { user } = useTelegram();
  const { currentUser } = useContext(CurrentUserContext);
  const { profile } = useContext(TranslationContext);
  const { personal } = profile;
  const [editingField, setEditingField] = useState(null);

  const data = useMemo(() => {
    const fullNameRaw = getPersonalDataValue(currentUser, ['name', 'full_name', 'fullName'])
      || getTelegramFullName(user)
      || (user?.id ? String(user.id) : '');
    const emailRaw = currentUser?.email || '';
    const phoneRaw = currentUser?.phone || currentUser?.phoneNumber || '';
    const dateOfBirthRaw = normalizeDateOfBirthApiValue(
      getPersonalDataValue(currentUser, ['dob', 'date_of_birth', 'dateOfBirth', 'birth_date'])
    );

    return {
      fullNameRaw,
      emailRaw,
      phoneRaw,
      dateOfBirthRaw,
      fullName: fullNameRaw || personal.notAdded,
      email: emailRaw || personal.notAdded,
      phone: formatConfirmedPhone(phoneRaw) || personal.notAdded,
      dateOfBirth: formatDateOfBirthDisplay(dateOfBirthRaw) || personal.notAdded,
      telegramUsername: getTelegramUsername(user),
    };
  }, [currentUser, personal, user]);

  const editInitialValues = useMemo(() => ({
    dob: data.dateOfBirthRaw,
    email: data.emailRaw,
    name: data.fullNameRaw,
    phone: data.phoneRaw,
  }), [data]);

  return (
    <section className="profile-subpage profile-personal">
      <ScreenHeader title={profile.personalInformation} onBack={() => navigate('/profile')} />

      <div className="profile-subpage__content">
        <button
          type="button"
          className="profile-personal__identity"
          onClick={() => {
            triggerImpactHaptic('light');
            setEditingField('name');
          }}
        >
          <TelegramUserAvatar className="profile-personal__avatar" />
          <span className="profile-personal__identity-copy">
            <span className="profile-personal__name">{data.fullName}</span>
            {data.telegramUsername ? (
              <span className="profile-personal__telegram">{data.telegramUsername}</span>
            ) : null}
          </span>
          <EditIcon className="profile-personal__edit-icon" />
        </button>

        <div className="profile-personal__fields">
          <InfoRow
            Icon={MailIcon}
            label={personal.email}
            value={data.email}
            onClick={() => setEditingField('email')}
          />
          <InfoRow
            Icon={PhoneIcon}
            label={personal.phone}
            value={data.phone}
            onClick={() => setEditingField('phone')}
          />
          <InfoRow
            Icon={CalendarIcon}
            label={personal.dateOfBirth}
            value={data.dateOfBirth}
            onClick={() => setEditingField('dob')}
          />
        </div>
      </div>

      <AnimatePresence>
        {editingField && (
          <PersonalInfoEditSheet
            field={editingField}
            initialValue={editInitialValues[editingField] || ''}
            onClose={() => setEditingField(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export default ProfilePersonalInfo;
